<template>
  <v-layout justify-center>
    <loading :active.sync="busy"/>

    <v-dialog v-model="dialog_status"
      class="dialog-container"
      persistent max-width="400px"
      scrollable
      @keydown.esc="close()">
      
      <v-card>
        <v-card-title>
          도큐먼트 복사/이동
        </v-card-title>

        <v-card-text v-if="!success">
          <div class="meta">기존 프로젝트를 선택하시거나, 프로젝트를 새로 만들고 선택해주세요.</div>
          <v-radio-group class="small-radio" v-model="copy_proj_id">
            <v-radio v-if="$route.name!='document-clip'"
              :disabled="user_type=='basic'"
              :key="clip.proj_id"
              label="도큐먼트 클립"
              :value="parseInt(clip.proj_id)">
            </v-radio>
            <v-radio v-for="value in projects"
              :key="value.proj_id"
              :label="value.title"
              :value="parseInt(value.proj_id)">
            </v-radio>
          </v-radio-group>
          <div class="create-project" v-if="create_possible">
            <v-text-field outlined class="title-field"
              v-model.trim="title"
              @input="resetError()"
              @blur="$v.title.$touch()"
              :error-messages="title_errors">
              <template v-slot:label>
                새 프로젝트 이름
              </template>
            </v-text-field>
            <v-btn small text class="primary" @click="createProject()">
              프로젝트 추가
            </v-btn>
          </div>
          <div v-if="error">
            <div class="message-error">
              {{ error_message }}
            </div>
          </div>
        </v-card-text>

        <v-card-text v-else>
          프로젝트 이동/복사가 완료되었습니다.
        </v-card-text>

        <v-card-actions v-if="!success">
          <v-spacer></v-spacer>
          <v-btn small text @click="close()">
            {{ $t('fork_doc.close') }}
          </v-btn>
          <v-btn small text color="cyan_blue"
            :disabled="!confirm"
            @click="copyDocuments('copy')">
            복사하기
          </v-btn>
          <v-btn small text color="pms205"
            :disabled="!confirm"
            @click="copyDocuments('move')">
            이동하기
          </v-btn>
        </v-card-actions>

        <v-card-actions v-else>
          <v-spacer></v-spacer>
          <v-btn small text @click="close()">
            {{ $t('fork_doc.close') }}
          </v-btn>
          <v-btn small depressed color="primary" @click="getProject()">
            {{ $t('fork_doc.go_project') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import { mapGetters } from 'vuex'
import { required } from 'vuelidate/lib/validators'

export default {
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },

    projid: {
      type: Number,
      required: true,
    },

    dockey: {
      type: Array,
      required: true,
    },

    editmode: {
      type: Boolean,
    }
  },

  data() {
    return {
      busy: false,
      success: false,
      error: false,
      error_message: "",
      copy_proj_id: null,

      title: "",
      is_duplicated: false,

      mode: null,
    }
  },

  validations: {
    title: {
      required,
      isUnique() {
        return this.$v.title.$dirty &&
          this.$v.title.required &&
          !this.is_duplicated
      }
    },
  },

  computed: {
    dialog_status: {
      get: function(value) {
        return this.dialog
      },
      set: function(value) {
        if (!value) { this.close() }
      },
    },

    ...mapGetters({
      raw_projects: 'project/projects',
      user_type: 'user/user_type',
    }),

    clip() {
      return this.raw_projects.filter(v => v.mode == 9)[0]
    },

    doc_key() {
      return JSON.parse(JSON.stringify(this.dockey))
    },

    projects() {
      let vm = this
      return !!this.raw_projects ?
        this.raw_projects.filter(v => v.proj_id!=vm.projid && v.status<10 && v.mode!=9) : []
    },

    create_possible() {
      let activated_projects = this.raw_projects.filter(v => v.status<10 && v.mode!=9)
      return this.user_type == 'basic' && activated_projects.length >= 3 ? false : true
    },

    confirm() {
      return !!this.copy_proj_id
    },

    project_title_list() {
      return this.projects.map(proj => proj.title)
    },

    title_errors() {
      const errors = []
      if (!this.$v.title.$dirty) return errors
      !this.$v.title.required && errors.push(this.$t('fork_mp.required'))
      !this.$v.title.isUnique && errors.push(this.$t('fork_mp.title_not_available'))
      return errors
    },
  },

  watch: {
    dialog_status(val) {
      if (val) {
        this.copy_proj_id = null
      }
    },

    copy_proj_id: {
      handler(val) {
        this.error = false
      },
      immediate: true
    },
  },

  methods: {
    close() {
      this.reset()
      this.$emit('update:editmode', false)
      this.$emit('update:dialog', false)
    },

    reset() {
      this.title = ""
      this.mode = 'copy'
      this.is_duplicated = false
      this.success = false
      this.resetError()
    },

    checkTitle() {
      let title_list = this.raw_projects.map(proj => proj.title)
      if (title_list.includes(this.title)) {
        this.is_duplicated = true
      } else { this.is_duplicated = false }
    },

    async createProject() {
      this.checkTitle()
      if (!this.$v.title.$anyError) {
        let error = false
        try {
          await this.$store.dispatch('project/createProject', {
            title: this.title
          })
        } catch(e) {
          this.$store.commit('error/setDialog', true)
          this.$store.commit('error/setErrorCode', e.response.status)
          error = true
        } finally {
          if (!error) {
            this.title = ""
            this.resetError()
          }
        }
      }
    },

    async copyDocuments(mode) {
      this.mode = mode
      this.busy = true
      try {
        await this.$store.dispatch('project/getDocumentsInToProject', {
          proj_id: this.copy_proj_id
        })
      } catch(e) {
        this.error = true
        this.error_message = this.$t('alert.try_again')
      } finally {
        if (!this.error) {
          let keys = this.$store.getters['project/to_documents'].map(v => v.key)
          let copy_keys = this.doc_key.filter(v => !keys.includes(v))
          try {
            if (!!copy_keys.length) {
              await this.$store.dispatch('project/addCopyDocumentsToProject', {
                proj_id: this.copy_proj_id,
                documents: copy_keys,
              })
            }
          } catch(e) {
            this.error = true
            this.error_message = this.$t('alert.try_again')
          } finally {
            try {
              if (mode == 'move') {
                await this.$store.dispatch('project/removeDocumentsFromProject', {
                  proj_id: this.projid,
                  documents: this.doc_key,
                })
              }
            } catch(e) {
              this.error = true
              this.error_message = this.$t('alert.try_again')
            } finally {
              if (!this.error) {
                this.success = true
              }
              this.busy = false
            }
          }
        }
      }
    },

    getProject() {
      try {
        this.close()
      } finally {
        this.$router.push({name: 'project', 
          params: {id: this.copy_proj_id}})
      }
    },

    resetError() {
      this.$v.$reset()
      this.is_duplicated = false
      this.disabled = false
    },
  },
  
}
</script>

<style scoped>
  .meta {
    margin-bottom: 12px;
  }

  .create-project {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .title-field {
    margin-right: 12px !important;
    transform: translateY(10px);
  }
</style>


// WEBPACK FOOTER //
// src/components/modal/project/CopyDocuments.vue